import { type GameState, getGame, type PlayerIndex, type RoomView } from "@playpal/core";
import { useEffect, useRef, useState } from "react";
import { GameView, turnHint } from "../games/GameView";
import { rememberGamePlayed, rememberPartner, rememberRoom } from "../lib/history";
import { isMuted, play, setMuted } from "../lib/sound";
import { useTurnAlert } from "../lib/turnAlert";
import type { RoomConnection } from "../lib/useRoom";
import { ArcadeRace } from "./ArcadeRace";
import { GamePicker } from "./GamePicker";
import {
	DifficultyPicker,
	FriendAway,
	MatchScore,
	ReactionBar,
	ReactionOverlay,
	TurnClock,
} from "./RoomExtras";
import { ShareRow } from "./ShareRow";
import { Button, Card, Panel, playerStyle } from "./ui";

interface Props {
	connection: RoomConnection;
	onLeave: () => void;
}

export function RoomScreen({ connection, onLeave }: Props) {
	const { room, seat, status, nudge, clearNudge, clockOffset } = connection;

	useNudgeTimeout(nudge, clearNudge);
	useResultChime(room?.state ?? null, seat);
	useFriendChime(room);
	// Hooks cannot sit behind the early return below, so this reads the turn
	// defensively rather than after `room` has been narrowed.
	useTurnAlert(room?.state?.turn != null && room.state.turn === seat && !room.state.result);
	useRemember(room, seat);

	if (!room || seat === null) {
		return <Joining status={status} onLeave={onLeave} />;
	}

	const friend = room.players[seat === 0 ? 1 : 0];
	const game = room.gameId ? getGame(room.gameId) : null;

	return (
		// Wider than the rest of the app on a tablet: this is the only screen with
		// something to put in the extra width, and the board is what it goes to.
		<Panel className="lg:max-w-5xl deck:max-w-6xl">
			<RoomBar
				room={room}
				onLeave={onLeave}
				onChangeGame={room.state || room.arcade ? connection.backToLobby : undefined}
			/>

			{status === "reconnecting" && (
				<p className="mb-4 rounded-2xl bg-amber-200 px-4 py-2 text-center font-semibold text-amber-950">
					Lost the connection — trying again…
				</p>
			)}

			{/* In a game the cards move into the rail beside the board; before one
			    starts there is no board to make room for, so they stay on top. */}
			{/*
			 * Not during a race. The cards are how you see who is who and what the
			 * score is, and an arcade race puts both of those above the board
			 * itself — so here they are ninety pixels of duplicate, on the one
			 * screen in the app where vertical room is what runs out first.
			 */}
			{(!friend || ((!room.state || !game) && !room.arcade)) && (
				<div className="mb-3">
					<PlayerCards room={room} seat={seat} />
				</div>
			)}

			{!friend ? (
				<WaitingForFriend code={room.code} />
			) : room.arcade ? (
				<ArcadeRace
					// Keyed on the seed, so a new race is a fresh component rather
					// than one carrying the last race's half-sent log about with it.
					key={room.arcade.seed}
					view={room.arcade}
					seat={seat}
					connected={status === "playing"}
					friendName={friend.name}
					onSubmit={connection.sendRun}
					onLanded={connection.runLanded}
					onAgain={() => room.arcade && connection.startArcade(room.arcade.arcadeId)}
					onLobby={connection.backToLobby}
				/>
			) : !room.state || !game ? (
				<GamePicker onPick={connection.pick} onArcade={connection.startArcade} />
			) : (
				/*
				 * Three grid areas rather than one column. Stacked they read in the
				 * order they always did; on a short landscape tablet the two rail
				 * groups sit beside the board instead of above and below it, which
				 * is the only way the board gets its height back.
				 */
				<div className="room-layout">
					<div className="[grid-area:rail-top] deck:space-y-3">
						<PlayerCards room={room} seat={seat} stacked />

						<FriendAway
							claimableAt={room.claimableAt}
							friendName={friend.name}
							clockOffset={clockOffset}
							onClaim={connection.claim}
						/>

						{room.difficulty && (
							<DifficultyPicker difficulty={room.difficulty} onChange={connection.setDifficulty} />
						)}

						{room.match && <MatchScore match={room.match} seat={seat} />}

						<TurnClock
							deadline={room.turnDeadline}
							turnSeconds={room.turnSeconds}
							clockOffset={clockOffset}
							yourTurn={room.state.turn === seat}
						/>
					</div>

					<div className="room-board [grid-area:board] min-w-0">
						<div className="mb-3 text-center">
							<h2 className="text-2xl font-black">
								{game.emoji} {game.title}
							</h2>
							<p className="text-lg font-semibold text-indigo-900/80 dark:text-indigo-100/80">
								{turnHint(room.state, seat, friend.name)}
							</p>
						</div>

						<GameView
							state={room.state}
							seat={seat}
							onMove={connection.move}
							clockOffset={clockOffset}
						/>

						{room.state.result && (
							<ResultBanner
								state={room.state}
								seat={seat}
								friendName={friend.name}
								match={room.match}
								onAgain={connection.again}
								onLobby={connection.backToLobby}
							/>
						)}
					</div>

					<div className="[grid-area:rail-bottom]">
						<ReactionBar onReact={connection.react} />

						<details className="mt-4 rounded-2xl bg-white/50 px-4 py-3 dark:bg-white/10">
							<summary className="cursor-pointer text-lg font-bold">How to play</summary>
							<ol className="mt-2 list-decimal space-y-1 pl-6 text-indigo-900/80 dark:text-indigo-100/80">
								{game.howTo.map((line) => (
									<li key={line}>{line}</li>
								))}
							</ol>
						</details>
					</div>
				</div>
			)}

			<ReactionOverlay reactions={connection.reactions} />

			{nudge && (
				<p
					role="status"
					className="fixed inset-x-4 bottom-6 mx-auto max-w-sm rounded-2xl bg-indigo-900/90 px-4 py-3 text-center text-lg font-semibold text-white shadow-xl"
				>
					{nudge}
				</p>
			)}
		</Panel>
	);
}

function RoomBar({
	room,
	onLeave,
	onChangeGame,
}: {
	room: RoomView;
	onLeave: () => void;
	/** Only offered while a game is on — it's how you bail out of one. */
	onChangeGame?: () => void;
}) {
	const [muted, setMutedState] = useState(isMuted);
	const [copied, setCopied] = useState(false);

	const copyCode = async () => {
		try {
			await navigator.clipboard.writeText(room.code);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard blocked — the code is on screen anyway.
		}
	};

	return (
		<header>
			{/*
			 * One row, and it has to stay one row on a narrow phone: this sat at
			 * the top of the most-looked-at screen in the app and wrapped onto
			 * three lines, pushing the board itself down the page. Sound and
			 * Change game are icons with labels for a screen reader, because
			 * their words were the widest thing here and the least needed.
			 */}
			<div className="mb-3 flex items-center gap-2">
				<button
					type="button"
					onClick={copyCode}
					aria-label={`Room code ${room.code.split("").join(" ")}. Tap to copy.`}
					className="rounded-xl bg-white/70 px-3 py-1.5 text-xl font-black tracking-[0.2em] shadow dark:bg-white/10"
				>
					{room.code}
					<span className="ml-1.5 align-middle text-xs font-semibold tracking-normal opacity-70">
						{copied ? "copied!" : "copy"}
					</span>
				</button>

				<div className="ml-auto flex items-center gap-1.5">
					{onChangeGame && (
						<button
							type="button"
							onClick={onChangeGame}
							aria-label="Change game"
							title="Change game"
							className="rounded-xl bg-white/70 px-3 py-2 text-lg shadow dark:bg-white/10"
						>
							🎲
						</button>
					)}
					<button
						type="button"
						aria-pressed={muted}
						aria-label={muted ? "Sound off" : "Sound on"}
						title={muted ? "Sound off" : "Sound on"}
						onClick={() => {
							const next = !muted;
							setMuted(next);
							setMutedState(next);
						}}
						className="rounded-xl bg-white/70 px-3 py-2 text-lg shadow dark:bg-white/10"
					>
						{muted ? "🔇" : "🔊"}
					</button>
					<Button tone="secondary" onClick={onLeave}>
						Leave
					</Button>
				</div>
			</div>
		</header>
	);
}

/**
 * Who is playing and what the score is.
 *
 * Two across when it sits above the board, one above the other when it has
 * moved into the rail beside it — where the width is what is scarce and the
 * height is not.
 */
function PlayerCards({
	room,
	seat,
	stacked = false,
}: {
	room: RoomView;
	seat: PlayerIndex;
	/** Set when the cards are in the rail, where width is what is scarce. */
	stacked?: boolean;
}) {
	return (
		<div className={`grid grid-cols-2 gap-3 ${stacked ? "deck:grid-cols-1" : ""}`}>
			{room.players.map((player, index) => {
				const style = playerStyle(index as PlayerIndex);
				const isMe = index === seat;
				const active = room.state?.turn === index;
				return (
					<div
						key={style.name}
						className={`rounded-2xl px-4 py-3 shadow-md transition ${style.soft} ${
							active ? `ring-4 ${style.ring}` : ""
						}`}
					>
						<div className="flex items-center gap-2">
							<span className="text-3xl" aria-hidden="true">
								{player?.avatar ?? "⏳"}
							</span>
							<div className="min-w-0">
								{/* Steps down on a narrow phone: two cards side by side leave about
								    120px for a name, and "Clever Koala (you)" does not fit at text-lg. */}
								<p className="truncate text-base font-bold sm:text-lg">
									{player ? player.name : "Waiting…"}
									{isMe && <span className="ml-1 text-sm opacity-70">(you)</span>}
								</p>
								<p className="text-sm opacity-80">
									{player ? `${player.wins} win${player.wins === 1 ? "" : "s"}` : "empty seat"}
									{player && !player.connected ? " · offline" : ""}
								</p>
							</div>
						</div>
						{room.state && <p className="mt-1 text-2xl font-black">{room.state.scores[index]}</p>}
					</div>
				);
			})}
		</div>
	);
}

function WaitingForFriend({ code }: { code: string }) {
	return (
		<Card className="text-center">
			<h2 className="text-3xl font-black">Share this code</h2>
			<p className="mt-2 text-indigo-900/70 dark:text-indigo-100/80">
				Read it out, send the link, or let them scan the square.
			</p>
			<p className="my-6 animate-wiggle text-6xl font-black tracking-[0.35em] text-indigo-700 dark:text-indigo-200">
				{code}
			</p>
			<ShareRow code={code} />
			<p className="mt-4 text-lg font-semibold">Waiting for your friend to arrive…</p>
		</Card>
	);
}

function ResultBanner({
	state,
	seat,
	friendName,
	match,
	onAgain,
	onLobby,
}: {
	state: GameState;
	seat: PlayerIndex;
	friendName: string;
	match: RoomView["match"];
	onAgain: () => void;
	onLobby: () => void;
}) {
	if (!state.result) return null;
	const { winner } = state.result;
	// A single game is just a game — "match" language only makes sense in a series.
	const matchOver = match != null && match.winner != null && match.bestOf > 1;
	const headline = matchOver
		? match?.winner === seat
			? "You win the match! 🏆"
			: `${friendName} wins the match! 👏`
		: winner === null
			? "It's a draw! 🤝"
			: winner === seat
				? "You win! 🎉"
				: `${friendName} wins! 👏`;

	return (
		<Card className="mt-6 text-center animate-pop">
			<h3 className="text-4xl font-black">{headline}</h3>
			<p className="mt-1 text-xl font-semibold">
				{state.scores[0]} – {state.scores[1]}
			</p>
			<div className="mt-5 flex flex-wrap justify-center gap-3">
				<Button size="lg" onClick={onAgain}>
					{matchOver ? "🔁 New match" : "▶️ Next game"}
				</Button>
				<Button size="lg" tone="secondary" onClick={onLobby}>
					🎲 Another game
				</Button>
			</div>
		</Card>
	);
}

/** Clears the "you can't do that" nudge after a couple of seconds. */
function useNudgeTimeout(nudge: string | null, clear: () => void) {
	useEffect(() => {
		if (!nudge) return;
		play("bad");
		const timer = setTimeout(clear, 2200);
		return () => clearTimeout(timer);
	}, [nudge, clear]);
}

function useResultChime(state: GameState | null, seat: PlayerIndex | null) {
	const previous = useRef<string | null>(null);
	useEffect(() => {
		const key = state?.result ? `${state.kind}:${state.result.winner}` : null;
		if (key && key !== previous.current && seat !== null) {
			play(state?.result?.winner === seat ? "win" : "lose");
		}
		previous.current = key;
	}, [state, seat]);
}

function useFriendChime(room: RoomView | null) {
	const previous = useRef(false);
	useEffect(() => {
		const both = Boolean(room?.players[0] && room?.players[1]);
		if (both && !previous.current) play("join");
		previous.current = both;
	}, [room]);
}

/**
 * The screen before a room exists — and the one place a child could get stuck.
 *
 * It used to be a single line of text with no way out. Joining a mistyped code,
 * or joining with no signal, leaves the socket retrying for ever and the room
 * never arrives; installed to a home screen there is no address bar and no back
 * button, and the code sits in the URL, so even a reload lands straight back
 * here. The way out has to be on this screen or it does not exist.
 */
function Joining({ status, onLeave }: { status: string; onLeave: () => void }) {
	const [waiting, setWaiting] = useState(0);

	useEffect(() => {
		const timer = setInterval(() => setWaiting((seconds) => seconds + 1), 1000);
		return () => clearInterval(timer);
	}, []);

	// Long enough that an ordinary slow join does not get called a failure.
	const struggling = waiting >= 8;

	return (
		<Panel>
			<Card className="text-center">
				<p className="text-2xl font-bold">
					{struggling
						? "Can't reach that game"
						: status === "reconnecting"
							? "Reconnecting…"
							: "Joining the room…"}
				</p>
				{struggling && (
					<p className="mt-2 text-lg opacity-80">
						Check the internet, or the code might be wrong. We'll keep trying.
					</p>
				)}
				<Button className="mt-5" tone="secondary" onClick={onLeave}>
					← Back
				</Button>
			</Card>
		</Panel>
	);
}

/**
 * Notes down the room and the game, so the front page can offer a way back in
 * and can tell a child how much of the catalogue they have actually seen.
 *
 * Only rooms with a person in them: rejoining a game against the computer is
 * pointless, since a new one is one tap away and identical.
 */
function useRemember(room: RoomView | null, seat: PlayerIndex | null) {
	const friend = seat === null ? null : (room?.players[seat === 0 ? 1 : 0] ?? null);
	const friendName = friend?.name ?? null;
	const friendAvatar = friend?.avatar ?? null;
	const friendCode = friend?.playerCode ?? null;
	// A person, as opposed to Robo. `mode === "friend"` already implies it, but
	// the claim this hook makes — "you played this friend" — deserves its own
	// check rather than an inference.
	const friendIsHuman = friend != null && !friend.isBot;
	const worthReturningTo = room?.mode === "friend";
	const code = room?.code ?? null;
	const gameId = room?.gameId ?? null;

	useEffect(() => {
		if (!code || !worthReturningTo) return;
		rememberRoom(
			code,
			friendName && friendAvatar ? { name: friendName, avatar: friendAvatar } : null,
		);
	}, [code, worthReturningTo, friendName, friendAvatar]);

	/*
	 * And the friend themselves, separately from the room: the room dies within
	 * hours, but "I want to play them again" outlasts it by days. Recorded the
	 * moment they are here rather than when a game ends, so a pair who only got
	 * as far as the lobby still find each other tomorrow. Re-runs if their
	 * player code arrives later — signing in mid-room upgrades the entry from
	 * a guest to a card-holder.
	 */
	useEffect(() => {
		if (!worthReturningTo || !friendIsHuman || !friendName || !friendAvatar) return;
		rememberPartner({ name: friendName, avatar: friendAvatar, playerCode: friendCode });
	}, [worthReturningTo, friendIsHuman, friendName, friendAvatar, friendCode]);

	useEffect(() => {
		if (gameId) rememberGamePlayed(gameId);
	}, [gameId]);
}
